/**
 * GameScoringPage for entering game scores.
 *
 * Tab-based interface for:
 * - 1st half score entry
 * - 2nd half score entry
 * - Results and game actions
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { judgeApi } from '@/api/client';
import { GameTabs } from '@/features/judge/components/GameTabs';
import { useGameState } from '@/stores/gameStore';
import type { HalfScore, EventType } from '@/types';
import { calculateScore } from '@/types';

export function GameScoringPage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    getGame,
    submitFirstHalf,
    submitSecondHalf,
    submitOvertime,
    editHalf,
    endGame,
    cancelGame,
  } = useGameState();

  const [error, setError] = useState<string | null>(null);

  const game = gameId ? getGame(gameId) : undefined;

  // Event submission mutation
  const submitEventMutation = useMutation({
    mutationFn: judgeApi.submitEvent,
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  // Redirect if game not found
  useEffect(() => {
    if (gameId && !game) {
      navigate('/judge');
    }
  }, [gameId, game, navigate]);

  if (!game) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmitFirstHalf = async (team1Score: HalfScore, team2Score: HalfScore) => {
    setError(null);

    // Submit to server
    const t1Total = calculateScore(team1Score.akka, team1Score.pappi);
    const t2Total = calculateScore(team2Score.akka, team2Score.pappi);

    try {
      await submitEventMutation.mutateAsync({
        paper_id: game.id,
        team1_id: game.team1.id,
        team2_id: game.team2.id,
        event: 'game_1st' as EventType,
        team1_score: t1Total,
        team2_score: t2Total,
      });

      // Update local state
      submitFirstHalf(game.id, team1Score, team2Score);
    } catch {
      // Error handled by mutation
    }
  };

  const handleSubmitSecondHalf = async (team1Score: HalfScore, team2Score: HalfScore) => {
    setError(null);

    const t1Total = calculateScore(team1Score.akka, team1Score.pappi);
    const t2Total = calculateScore(team2Score.akka, team2Score.pappi);

    try {
      await submitEventMutation.mutateAsync({
        paper_id: game.id,
        team1_id: game.team1.id,
        team2_id: game.team2.id,
        event: 'game_2nd' as EventType,
        team1_score: t1Total,
        team2_score: t2Total,
      });

      submitSecondHalf(game.id, team1Score, team2Score);
    } catch {
      // Error handled by mutation
    }
  };

  const handleSubmitOvertime = async (team1Score: HalfScore, team2Score: HalfScore) => {
    setError(null);

    const t1Total = calculateScore(team1Score.akka, team1Score.pappi);
    const t2Total = calculateScore(team2Score.akka, team2Score.pappi);

    try {
      await submitEventMutation.mutateAsync({
        paper_id: game.id,
        team1_id: game.team1.id,
        team2_id: game.team2.id,
        event: 'game_ot' as EventType,
        team1_score: t1Total,
        team2_score: t2Total,
      });

      submitOvertime(game.id, team1Score, team2Score);

      // If overtime resolves the tie, auto-end the game
      if (t1Total !== t2Total) {
        await handleEndGame();
      }
    } catch {
      // Error handled by mutation
    }
  };

  const handleEditHalf = (half: 'firstHalf' | 'secondHalf') => {
    editHalf(game.id, half);
  };

  const handleEndGame = async () => {
    setError(null);

    // Calculate final scores for the end event
    let team1Total = 0;
    let team2Total = 0;

    if (game.overtime.ready) {
      team1Total = calculateScore(game.overtime.team1.akka, game.overtime.team1.pappi);
      team2Total = calculateScore(game.overtime.team2.akka, game.overtime.team2.pappi);
    } else {
      const t1First = calculateScore(game.firstHalf.team1.akka, game.firstHalf.team1.pappi);
      const t2First = calculateScore(game.firstHalf.team2.akka, game.firstHalf.team2.pappi);
      const t1Second = calculateScore(game.secondHalf.team1.akka, game.secondHalf.team1.pappi);
      const t2Second = calculateScore(game.secondHalf.team2.akka, game.secondHalf.team2.pappi);
      team1Total = t1First + t1Second;
      team2Total = t2First + t2Second;
    }

    try {
      await submitEventMutation.mutateAsync({
        paper_id: game.id,
        team1_id: game.team1.id,
        team2_id: game.team2.id,
        event: 'game_end' as EventType,
        team1_score: team1Total,
        team2_score: team2Total,
      });

      endGame(game.id);
      // Invalidate judge queue so it refetches without the finished game
      await queryClient.invalidateQueries({ queryKey: ['judgeQueue'] });
      navigate('/judge');
    } catch {
      // Error handled by mutation
    }
  };

  const handleCancelGame = async () => {
    setError(null);

    try {
      await submitEventMutation.mutateAsync({
        paper_id: game.id,
        team1_id: game.team1.id,
        team2_id: game.team2.id,
        event: 'game_reset' as EventType,
      });

      // Only clear local state if backend reset succeeded
      cancelGame(game.id);
      await queryClient.invalidateQueries({ queryKey: ['judgeQueue'] });
      navigate('/judge');
    } catch {
      // Backend rejected reset (e.g., game already ended)
      // Show error message - don't clear local state
      setError('Pelin nollaus epäonnistui. Peli saattaa olla jo päättynyt.');
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Back button - allows escaping even in error state */}
      <Button
        component={RouterLink}
        to="/judge"
        variant="text"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Takaisin
      </Button>

      <GameTabs
        game={game}
        onSubmitFirstHalf={handleSubmitFirstHalf}
        onSubmitSecondHalf={handleSubmitSecondHalf}
        onSubmitOvertime={handleSubmitOvertime}
        onEditHalf={handleEditHalf}
        onEndGame={handleEndGame}
        onCancelGame={handleCancelGame}
        isSubmitting={submitEventMutation.isPending}
      />
    </Box>
  );
}


